import React, {
  BaseSyntheticEvent,
  createContext,
  Fragment,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'
import { AiOutlineSync } from 'react-icons/ai'

import {
  ArrowsRightLeftIcon,
  ChevronRightIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  MinusIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'

import {
  BasicType,
  FilterCodeDatePickerRange,
  FilterCodePriceRange,
  SelectInterface,
} from '../../interfaces/InterfacesLocal'
import {
  BasicNomenclator,
  filter_types,
} from '../../interfaces/ServerInterfaces'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { setTitlesForExport } from '../../store/slices/nomenclatorSlice'
import { setSpecificColumnSpaceToSave } from '../../store/slices/sessionSlice'
import { getTableId } from '../../utils/helpers'
import SelectOrderby from '../forms/SelectOrderby'
import FilterBadge from '../genericTable/FilterBadge'
import FilterComponent from '../genericTable/FilterComponent'
import Button from './Button'
import EmptyList from './EmptyList'
import Modal from './GenericModal'
import MultipleActBtn, { BtnActions } from './MultipleActBtn'
import MultipleFilterBtn from './MultipleFilterBtn'
import SpinnerLoading from './SpinnerLoading'

export interface SearchingInterface {
  action: (_searchTerm: string | null) => void // Callback Function to control Searching action
  placeholder?: string
  searchDelay?: boolean
}

export interface DataTableInterface {
  rowId?: string | number
  rowColor?: string
  deletedRow?: boolean
  rowWihtoutIcon?: boolean
  boldRow?: boolean
  borderTop?: boolean
  borderBottom?: boolean
  childRows?: {
    rowId?: string | number
    payload: Record<string, string | number | React.ReactNode>
  }[]
  payload: Record<string, string | number | React.ReactNode>
}

export interface FilterOpts {
  name: string
  format: filter_types
  filterCode: string
  data?: SelectInterface[]
  icon?: React.ReactNode
  datepickerRange?: FilterCodeDatePickerRange[]
  priceRange?: FilterCodePriceRange[]
  isUntilToday?: boolean
  includingTime?: boolean
  dependentOn?: string //code del filtro del cual es dependiente esta entrada
  asyncData?: {
    url: string
    defaultParams?: BasicType
    idCode: string
    dataCode: string | string[]
  }
  defaultValue?: unknown
}

interface FilterComponentInterface {
  availableFilters: FilterOpts[]
  filterAction: (_data: unknown | null) => void
}

interface ExtendedNomenclator extends BasicNomenclator {
  action?: (_data: number) => void
  availableOptions: OptionsNomenclator[]
  reset: () => void
}

interface OptionsNomenclator {
  name: string
  id: number
}

interface orderByComponent {
  availableFilters: ExtendedNomenclator[]
}

interface GenericTableProps {
  tableTitles: string[]
  tableData: DataTableInterface[]
  rowAction?: (_id?: string | number) => void //Callback function to control click on each row. It work with id param
  childRowAction?: (
    _childId: string | number,
    _parentId?: string | number
  ) => void //Callback function to control click on each childRow. It work with id param
  searching?: SearchingInterface
  loading?: boolean
  paginateComponent?: React.ReactNode
  actions?: BtnActions[]
  filterComponent?: FilterComponentInterface
  syncAction?: { action: () => void; loading: boolean }
  childRowsAlwaysActive?: boolean
  genericTableHeigth96?: boolean
  rowActionDeleteIcon?: boolean
  lastColumnInGray?: boolean
  orderBy?: orderByComponent
  groupBy?: orderByComponent
  showSpecificColumns?: boolean
  notShadowBotton?: boolean
  specificColumnSpaceToSave?: string
  classRowVoid?: string
  iconTable?: React.ReactNode
  headSticky?: boolean
  maxTableHeight?: string
  showInitFilters?: boolean
  refresh?: boolean
  onRefresh?: () => void
  onSelectColumnShow?: (_data: string[]) => void
  columnClassnames?: {
    columnName: string
    className: string
  }[]
  defaultColumnsShowed?: { name: string; id: number }[]
  headerInfo?: ReactNode
}

//TABLE CONTEXT
export interface TableContextInterface {
  selectedFilter: BasicNomenclator[]
  setSelectedFilter: React.Dispatch<React.SetStateAction<BasicNomenclator[]>>
  setFilterActived?: React.Dispatch<React.SetStateAction<boolean>>
}

export const TableContext = createContext<TableContextInterface>({
  selectedFilter: [],
  setSelectedFilter: (() => undefined) as React.Dispatch<
    React.SetStateAction<BasicNomenclator[]>
  >,
})

export const ContextTable = ({
  children,
  values,
}: {
  children: ReactNode
  values: TableContextInterface
}) => {
  return (
    <TableContext.Provider value={values}>{children}</TableContext.Provider>
  )
}

const GenericTable = ({
  tableTitles,
  tableData,
  rowAction,
  rowActionDeleteIcon,
  childRowAction,
  searching,
  loading,
  paginateComponent,
  actions,
  filterComponent,
  syncAction,
  childRowsAlwaysActive,
  genericTableHeigth96,
  lastColumnInGray,
  orderBy,
  groupBy,
  showSpecificColumns,
  specificColumnSpaceToSave,
  iconTable,
  notShadowBotton,
  classRowVoid,
  headSticky,
  maxTableHeight,
  showInitFilters,
  onSelectColumnShow,
  refresh = false,
  onRefresh,
  defaultColumnsShowed,
  columnClassnames,
  headerInfo,
}: GenericTableProps) => {
  const keywords = [
    'agregar',
    'añadir',
    'nuevo',
    'nueva',
    'insertar',
    'alta',
    'adjuntar',
    'administrar',
  ]
  const addAction = actions?.find((actn) =>
    keywords.some((keyword) => actn.title.toLocaleLowerCase().includes(keyword))
  )

  const dispatch = useAppDispatch()

  const { specificColumnSpaceToSave: specificColumnSpaceToSaveData } =
    useAppSelector((state) => state.session)

  const [selectedTableTitles, setSelectedTableTitles] = useState<
    { name: string; id: number }[]
  >(defaultColumnsShowed || [])

  const [isMobileView, setIsMobileView] = useState(false)

  useEffect(() => {
    if (showSpecificColumns && specificColumnSpaceToSave) {
      if (specificColumnSpaceToSaveData === undefined) {
        dispatch(
          setSpecificColumnSpaceToSave([
            {
              spaceToSave: specificColumnSpaceToSave,
              selectableTitles: defaultColumnsShowed || [
                {
                  id: 0,
                  name: tableTitles[0],
                },
                {
                  id: 3,
                  name: tableTitles[3],
                },
                {
                  id: tableTitles.length - 1,
                  name: tableTitles[tableTitles.length - 1],
                },
              ],
            },
          ])
        )

        setSelectedTableTitles(
          defaultColumnsShowed
            ? defaultColumnsShowed
            : [
                {
                  name: tableTitles[0],
                  id: 0,
                },
                {
                  name: tableTitles[3],
                  id: 3,
                },
                {
                  name: tableTitles[tableTitles.length - 1],
                  id: tableTitles.length - 1,
                },
              ]
        )
      } else {
        const areaIncluded: boolean =
          specificColumnSpaceToSaveData.some(
            (elem: { spaceToSave: string }) =>
              elem.spaceToSave === specificColumnSpaceToSave
          ) ?? false

        if (areaIncluded) {
          if (specificColumnSpaceToSaveData.length > 0) {
            setSelectedTableTitles(
              specificColumnSpaceToSaveData
                .find(
                  (elem: { spaceToSave: string }) =>
                    elem.spaceToSave === specificColumnSpaceToSave
                )
                ?.selectableTitles.map((itm) => {
                  return {
                    id: itm?.id || 0,
                    name: tableTitles[itm?.id || 0] || '',
                  }
                }) ?? []
            )
          } else {
            dispatch(
              setSpecificColumnSpaceToSave([
                ...specificColumnSpaceToSaveData,
                {
                  spaceToSave: specificColumnSpaceToSave,
                  selectableTitles: defaultColumnsShowed || [
                    {
                      id: 0,
                      name: tableTitles[0],
                    },
                    {
                      id: 3,
                      name: tableTitles[3],
                    },
                    {
                      id: tableTitles.length - 1,
                      name: tableTitles[tableTitles.length - 1],
                    },
                  ],
                },
              ])
            )

            setSelectedTableTitles(
              defaultColumnsShowed || [
                {
                  name: tableTitles[0],
                  id: 0,
                },
                {
                  name: tableTitles[3],
                  id: 3,
                },
                {
                  name: tableTitles[tableTitles.length - 1],
                  id: tableTitles.length - 1,
                },
              ]
            )
          }
        } else {
          dispatch(
            setSpecificColumnSpaceToSave([
              ...specificColumnSpaceToSaveData,
              {
                spaceToSave: specificColumnSpaceToSave,
                selectableTitles: [
                  {
                    id: 0,
                    name: tableTitles[0],
                  },
                  {
                    id: 3,
                    name: tableTitles[3],
                  },
                  {
                    id: tableTitles.length - 1,
                    name: tableTitles[tableTitles.length - 1],
                  },
                ],
              },
            ])
          )

          setSelectedTableTitles(
            defaultColumnsShowed || [
              {
                name: tableTitles[0],
                id: 0,
              },
              {
                name: tableTitles[3],
                id: 3,
              },
              {
                name: tableTitles[tableTitles.length - 1],
                id: tableTitles.length - 1,
              },
            ]
          )
        }
      }
    } else {
      setSelectedTableTitles(
        defaultColumnsShowed || [
          {
            name: tableTitles[0],
            id: 0,
          },
          {
            name: tableTitles[3],
            id: 3,
          },
          {
            name: tableTitles[tableTitles.length - 1],
            id: tableTitles.length - 1,
          },
        ]
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => {
    if (showSpecificColumns) {
      const idTable = getTableId(tableTitles)
      const tableColumns = JSON.parse(
        localStorage.getItem('tableColumns') ?? '{}'
      )

      if (tableColumns?.[idTable])
        setSelectedTableTitles(tableColumns[idTable] || [])
      else {
        if (selectedTableTitles.length > 0) {
          tableColumns[idTable] = selectedTableTitles
          // console.log(selectedTableTitles)
          localStorage.setItem('tableColumns', JSON.stringify(tableColumns))
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)') // < 768
    const handleMediaChange = (e: MediaQueryListEvent) => {
      setIsMobileView(e.matches)
    }

    // Establecer el valor inicial
    setIsMobileView(mediaQuery.matches)

    mediaQuery.addEventListener('change', handleMediaChange)

    return () => {
      mediaQuery.removeEventListener('change', handleMediaChange)
    }
  }, [])

  const renderMobileCard = (info: DataTableInterface, key: number) => {
    // Obtener las columnas visibles en el orden correcto
    const visibleColumns = showSpecificColumns
      ? selectedTableTitles.sort((a, b) => a.id - b.id) // Ordenar por ID para mantener el orden original
      : tableTitles.map((name, id) => ({ name, id }))

    return (
      <div
        className={
          `mb-4 rounded-md border bg-card p-4 shadow-sm ` +
          `${rowAction ? 'cursor-pointer hover:bg-accent/50' : ''} ` +
          `${info.deletedRow ? 'line-through' : ''} ` +
          `${info.boldRow ? 'font-bold' : ''} ` +
          `${info.borderTop ? 'border-t-2 border-t-gray-500' : ''} ` +
          `${info.borderBottom ? 'border-b-2 border-b-gray-500' : ''} ` +
          `${info.rowColor ? `bg-${info.rowColor}` : ''}`
        }
        key={key}
        onClick={() => rowAction && rowAction(info.rowId)}
      >
        <div className="grid grid-cols-2 gap-3">
          {visibleColumns.map((column) => {
            const columnName = tableTitles[column.id]
            const cellValue = info.payload[columnName]

            // Si es la primera columna y tiene childRows, mostramos el icono
            if (column.id === 0 && info.childRows) {
              return (
                <div
                  className="col-span-2 mb-2 flex items-center gap-2"
                  key={column.id}
                >
                  <div className="flex-1">
                    <div className="text-xs font-medium text-muted-foreground">
                      {columnName}
                    </div>
                    <div>{cellValue}</div>
                  </div>
                  <span
                    className={
                      'flex h-5 w-5 cursor-pointer items-center ' +
                      'justify-center rounded-full border-2 border-gray-400'
                    }
                    onClick={(e) => {
                      e.stopPropagation()
                      if (info.rowId == null) return
                      changeChildRow(info.rowId)
                    }}
                  >
                    {!childRowsAlwaysActive ||
                    childRowsAlwaysActive === undefined ? (
                      currentParent === info.rowId ? (
                        <MinusIcon className="h-3 font-bold" />
                      ) : (
                        <PlusIcon className="h-3 font-bold" />
                      )
                    ) : currentParent === info.rowId ? (
                      <MinusIcon className="h-3 font-bold" />
                    ) : (
                      <PlusIcon className="h-3 font-bold" />
                    )}
                  </span>
                </div>
              )
            }

            // Solo mostrar columnas con datos
            if (
              cellValue === undefined ||
              cellValue === null ||
              cellValue === ''
            ) {
              return null
            }

            return (
              <div className="mb-2" key={column.id}>
                <div className="text-xs font-medium text-muted-foreground">
                  {columnName}
                </div>
                <div>{cellValue}</div>
              </div>
            )
          })}

          {/* Acción de la fila (icono) - Solo si hay rowAction y no es una columna vacía */}
          {rowAction && info.rowWihtoutIcon !== true && (
            <div className="col-span-2 mb-2 flex items-end justify-end">
              {rowActionDeleteIcon ? (
                <TrashIcon className="h-5 text-red-500" />
              ) : (
                <ChevronRightIcon className="h-5" />
              )}
            </div>
          )}
        </div>

        {/* Render child rows en móvil */}
        {info.childRows && currentParent === info.rowId && (
          <div className="mt-4 border-t pt-4">
            {info.childRows.map((subItem, idx) => (
              <div
                className={
                  `mb-3 rounded-md border bg-gray-50 p-3 ` +
                  `${childRowAction ? 'cursor-pointer' : ''}`
                }
                key={idx}
                onClick={() =>
                  childRowAction &&
                  childRowAction(subItem?.rowId || '', info.rowId)
                }
              >
                <div className="grid grid-cols-2 gap-3">
                  {visibleColumns.map((column) => {
                    const columnName = tableTitles[column.id]
                    const cellValue = subItem?.payload[columnName] || ''

                    // Solo mostrar columnas con datos
                    if (
                      cellValue === undefined ||
                      cellValue === null ||
                      cellValue === ''
                    ) {
                      return null
                    }

                    return (
                      <div key={column.id}>
                        <div className="text-xs font-medium text-muted-foreground">
                          {columnName}
                        </div>
                        <div>{cellValue}</div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  const updateTitlesForExport = () => {
    dispatch(
      setTitlesForExport(selectedTableTitles.map((selected) => selected.name))
    )
  }

  const createSelectableTitles = () => {
    return selectedTableTitles.map((selected) => ({
      id: tableTitles.indexOf(selected.name),
      name: selected.name,
    }))
  }

  const updateSpecificColumnSpace = () => {
    if (!specificColumnSpaceToSaveData) return

    const areaIncluded = specificColumnSpaceToSaveData.some(
      (elem) => elem.spaceToSave === specificColumnSpaceToSave
    )

    if (areaIncluded) {
      dispatch(
        setSpecificColumnSpaceToSave(
          specificColumnSpaceToSaveData.map((elem) =>
            elem.spaceToSave === specificColumnSpaceToSave
              ? {
                  ...elem,
                  selectableTitles: createSelectableTitles(),
                }
              : elem
          )
        )
      )
    } else {
      dispatch(
        setSpecificColumnSpaceToSave([
          ...specificColumnSpaceToSaveData,
          {
            spaceToSave: specificColumnSpaceToSave,
            selectableTitles: createSelectableTitles(),
          },
        ])
      )
    }
  }

  useEffect(() => {
    if (showSpecificColumns) {
      updateTitlesForExport()
      if (specificColumnSpaceToSaveData) {
        updateSpecificColumnSpace()
      }
    }
    if (onSelectColumnShow)
      onSelectColumnShow(selectedTableTitles.map((i) => i.name))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTableTitles, showSpecificColumns])

  //Debounce for filter -----------------------------------------------------------------------------
  const time = useRef<ReturnType<typeof setTimeout> | null>(null)

  const onChange = (e: BaseSyntheticEvent) => {
    if (searching && searching.searchDelay === false) {
      const value = e.target.value
      if (value !== '') {
        if (searching) searching.action(value)
        setSearchValue(value)
      } else {
        if (searching) searching.action(null)
        setSearchValue(null)
      }
    } else {
      if (time.current) clearTimeout(time.current)
      time.current = setTimeout(() => {
        const value = e.target.value
        if (value !== '') {
          if (searching) searching.action(value)
          setSearchValue(value)
        } else {
          if (searching) searching.action(null)
          setSearchValue(null)
        }
      }, 1200)
    }
  }

  //----------------------------------------------------------------------------------

  const [btnSearch, setBtnSearch] = useState(false)
  const [searchValue, setSearchValue] = useState<string | null>(null)
  const [selectedFilter, setSelectedFilter] = useState<BasicNomenclator[]>([])

  const [filterView, setFilterView] = useState(
    showInitFilters ? showInitFilters : false
  )

  useEffect(() => {
    if (searchValue === null && btnSearch) setBtnSearch(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue])

  //----------------------------------------------------------------------------------------------------

  //Manage Filter --------------------------------------------------------------------------------------
  const [filterActived, setFilterActived] = useState(false)

  //-----------------------------------------------------------------------------------------------------

  //Manage Child Rows------------------------------------------------------------------------------------
  const [currentParent, setCurrentParent] = useState<string | number | null>(
    null
  )

  useEffect(() => {
    if (currentParent) {
      const elements = document.querySelectorAll(`.parent-${currentParent}`)
      elements.forEach((elem) => {
        elem.removeAttribute('hidden')
      })
    }
  }, [currentParent, tableData])

  const changeChildRow = (id: string | number) => {
    const elements = document.querySelectorAll(`.parent-${currentParent}`)
    elements.forEach((elem) => {
      if (!childRowsAlwaysActive || childRowsAlwaysActive === undefined) {
        elem.setAttribute('hidden', 'true')
      } else {
        elem.setAttribute('hidden', 'false')
      }
    })
    if (currentParent === id) {
      setCurrentParent(null)
    } else {
      setCurrentParent(id)
    }
  }

  //-----------------------------------------------------------------------------------------------------

  const [selectedFilterOrder, setSelectedFilterOrder] = useState<
    ExtendedNomenclator[]
  >([])
  const [selectedFilterGroup, setSelectedFilterGroup] = useState<
    ExtendedNomenclator[]
  >([])

  function compare_update_arrays(
    array1: ExtendedNomenclator[],
    array2: ExtendedNomenclator[]
  ) {
    const array = array2.filter((elemento2) => {
      return !array1.some((elemento1) => elemento1.id === elemento2.id)
    })
    setSelectedFilterOrder(array)
  }
  const btnOrderbyAction = (e: ExtendedNomenclator[]) => {
    compare_update_arrays(selectedFilterOrder, e)
  }

  function compare_update_arrays_group(
    array1: ExtendedNomenclator[],
    array2: ExtendedNomenclator[]
  ) {
    const array = array2.filter((elemento2) => {
      return !array1.some((elemento1) => elemento1.id === elemento2.id)
    })
    if (array.length === 0 && selectedFilterGroup.length > 0) {
      if (typeof selectedFilterGroup[0].reset === 'function') {
        selectedFilterGroup[0].reset()
      }
      setSelectedFilterGroup(array)
      return
    }
    setSelectedFilterGroup(array)
    if (array.length === 0) return
    const id = array[0].id as number
    if (typeof array[0].action === 'function' && typeof id === 'number') {
      array[0].action(id)
    }
  }

  const btnGroupbyAction = (e: ExtendedNomenclator[]) => {
    compare_update_arrays_group(selectedFilterGroup, e)
  }

  return (
    <ContextTable
      values={{
        selectedFilter,
        setSelectedFilter,
        setFilterActived,
      }}
    >
      <div
        className={`flex h-full flex-col ${genericTableHeigth96 ? 'h-screen' : ''}`}
      >
        <div className="w-full align-middle">
          <div
            className={` ${notShadowBotton ? '' : `shadow ring-1 ring-black ring-opacity-5 md:rounded-lg`}`}
          >
            {(searching ||
              actions ||
              filterComponent ||
              syncAction ||
              orderBy ||
              showSpecificColumns ||
              iconTable ||
              headerInfo) && (
              <div className={`flex w-full items-center gap-3 px-5 py-3`}>
                {orderBy && (
                  <div
                    className={
                      selectedFilterOrder.length > 0
                        ? 'flex flex-1 items-center justify-between'
                        : 'flex flex-1 items-center justify-end'
                    }
                  >
                    <div className={'flex gap-5 p-2'}>
                      {selectedFilterOrder.map((e) => (
                        <SelectOrderby
                          action={e.action}
                          data={e.availableOptions}
                          key={e.id}
                          label={e.name}
                          reset={e.reset}
                        />
                      ))}
                    </div>
                    <MultipleFilterBtn
                      btnName="Ordenar por"
                      data={orderBy.availableFilters}
                      selected={selectedFilterOrder}
                      setSelected={btnOrderbyAction}
                    />
                  </div>
                )}

                {groupBy && (
                  <MultipleFilterBtn
                    btnName="Agrupar por"
                    data={groupBy.availableFilters}
                    selected={selectedFilterGroup}
                    setSelected={btnGroupbyAction}
                  />
                )}

                {searching && (
                  <>
                    <div
                      className={`flex w-full bg-inherit ${!(filterComponent || actions) && 'm-auto'}`}
                    >
                      <div className="relative w-full text-gray-400">
                        <div className="pointer-events-none absolute inset-y-0 left-0 z-0 flex items-center pl-3">
                          <MagnifyingGlassIcon
                            aria-hidden="true"
                            className="h-5 w-5"
                          />
                        </div>
                        <input
                          className={
                            'w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-3 leading-5 ' +
                            'text-gray-300 placeholder-gray-400 focus:border-transparent focus:bg-opacity-100' +
                            'focus:text-gray-900 focus:placeholder-gray-500 focus:outline-none focus:ring-0 sm:text-sm'
                          }
                          id="search"
                          name="search"
                          onChange={onChange}
                          placeholder={searching?.placeholder || ''}
                          type="search"
                        />
                      </div>
                    </div>
                  </>
                )}

                {syncAction && (
                  <AiOutlineSync
                    className={`cursor-pointer text-lg ${syncAction.loading ? 'animate-spin' : ''}`}
                    onClick={() => syncAction.action()}
                  />
                )}

                {refresh && (
                  <button
                    className={
                      'relative inline-flex items-center gap-2 rounded-md border-none bg-white px-2 py-2 ' +
                      'text-gray-700 ring-1 ring-inset ring-gray-300 focus:z-10 hover:bg-gray-50'
                    }
                    onClick={() => (onRefresh ? onRefresh() : null)}
                  >
                    <ArrowsRightLeftIcon className="h-5 cursor-pointer" />
                  </button>
                )}

                {!!filterComponent &&
                  filterComponent.availableFilters.length > 0 && (
                    <button
                      className={
                        'relative inline-flex items-center gap-2 rounded-md border-none bg-white px-2 py-2 ' +
                        'text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 focus:z-10' +
                        'hover:bg-gray-50'
                      }
                      onClick={() => setFilterView(true)}
                    >
                      Filtros
                    </button>
                  )}

                {filterView && (
                  <Modal
                    close={() => setFilterView(false)}
                    size="m"
                    state={filterView}
                  >
                    <FilterComponent
                      closeModal={() => setFilterView(false)}
                      filterActived={filterActived}
                      filterComponent={filterComponent}
                      setFilterActived={setFilterActived}
                    />
                  </Modal>
                )}

                {showSpecificColumns && (
                  <MultipleFilterBtn
                    btnName="Mostrar columnas"
                    data={
                      tableTitles.map((title, id) => {
                        return {
                          name: title,
                          id: id,
                        }
                      }) ?? []
                    }
                    selected={selectedTableTitles}
                    setSelected={(newArray: { name: string; id: number }[]) => {
                      setSelectedTableTitles(newArray)
                      if (showSpecificColumns) {
                        const tableColumns = JSON.parse(
                          localStorage.getItem('tableColumns') ?? '{}'
                        )
                        // console.log(tableColumns)

                        tableColumns[getTableId(tableTitles)] = newArray
                        localStorage.setItem(
                          'tableColumns',
                          JSON.stringify(tableColumns)
                        )
                      }
                    }}
                  />
                )}

                {headerInfo && (
                  <div className="flex-1 text-center">{headerInfo}</div>
                )}

                {actions &&
                  actions.length > 0 &&
                  tableData &&
                  tableData.length !== 0 && (
                    <>
                      {actions.length === 1 ? (
                        <Button
                          action={actions[0].action}
                          className="bg-slate-800 hover:bg-slate-900"
                          icon={actions[0].icon}
                          name={actions[0].title}
                        />
                      ) : (
                        <MultipleActBtn items={actions} />
                      )}
                    </>
                  )}

                {iconTable && (
                  <div className="inline-flex flex-shrink-0">{iconTable}</div>
                )}
              </div>
            )}

            {selectedFilter.length !== 0 && (
              <FilterBadge filterComponent={filterComponent} />
            )}

            <div
              className={
                'h-full w-full overflow-auto ' +
                (maxTableHeight ? `max-h-[${maxTableHeight}]` : '')
              }
            >
              {isMobileView ? (
                <div className="p-4">
                  {loading ? (
                    <div className="flex justify-center py-8">
                      <SpinnerLoading />
                    </div>
                  ) : tableData.length !== 0 ? (
                    tableData.map((info, key) => renderMobileCard(info, key))
                  ) : (
                    <div className="flex h-full w-full flex-col items-center justify-center p-24">
                      <EmptyList />
                      <div className="flex w-full items-center justify-center gap-2">
                        {!!addAction && !filterActived && (
                          <div className="-mt-4">
                            <Button
                              action={addAction.action}
                              color="slate-500"
                              icon={addAction.icon}
                              name={addAction.title}
                            />
                          </div>
                        )}
                        {filterActived && (
                          <div className="-mt-4">
                            <Button
                              action={() => {
                                filterComponent?.filterAction(null)
                                setSelectedFilter([])
                                setFilterActived(false)
                              }}
                              color="slate-500"
                              icon={<FunnelIcon className="h-5" />}
                              name="Limpiar filtro"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead
                    className={`bg-gray-50 ${headSticky ? 'sticky top-0' : ''}`}
                  >
                    <tr>
                      {selectedTableTitles?.find(
                        (selectTitle) => selectTitle?.id === 0
                      ) && (
                        <th
                          className={
                            'max-w-xs break-words py-3.5 pl-4 pr-3 text-left text-sm font-semibold ' +
                            'text-gray-900 first-letter:uppercase sm:pl-6'
                          }
                          scope="col"
                        >
                          {tableTitles[0]}
                        </th>
                      )}

                      {tableTitles.slice(1).map((title, index) => {
                        if (
                          showSpecificColumns !== undefined &&
                          showSpecificColumns
                        ) {
                          if (
                            selectedTableTitles &&
                            selectedTableTitles.find(
                              (selectTitle) => selectTitle?.name === title
                            )
                          ) {
                            return (
                              <th
                                className={
                                  'max-w-xs break-words px-3 py-3.5 text-center text-sm font-semibold text-gray-900 ' +
                                  'first-letter:uppercase' +
                                  `${lastColumnInGray && tableTitles.length === index + 2 ? 'bg-gray-300' : ''} ` +
                                  `${columnClassnames?.find((i) => i.columnName === title)?.className || ''}`
                                }
                                key={index}
                                scope="col"
                              >
                                {title}
                              </th>
                            )
                          }
                        } else {
                          return (
                            <th
                              className={
                                'max-w-xs break-words px-3 py-3.5 text-center text-sm font-semibold text-gray-900 ' +
                                'first-letter:uppercase' +
                                `${lastColumnInGray && tableTitles.length === index + 2 ? 'bg-gray-300' : ''} ` +
                                `${columnClassnames?.find((i) => i.columnName === title)?.className || ''}`
                              }
                              key={index}
                              scope="col"
                            >
                              {title}
                            </th>
                          )
                        }
                      })}

                      {rowAction && (
                        <th
                          className="relative py-3.5 pl-3 pr-4 first-letter:uppercase sm:pr-6"
                          scope="col"
                        >
                          <span className="sr-only">Edit</span>
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {loading && loading === true ? (
                      <tr>
                        <td
                          className="px-3 py-5 text-center text-sm text-gray-500"
                          colSpan={tableTitles.length + 1}
                        >
                          <SpinnerLoading />
                        </td>
                      </tr>
                    ) : tableData.length !== 0 ? (
                      tableData.map((info, key) => (
                        <Fragment key={key}>
                          <tr
                            className={` ${key % 2 === 0 ? '' : 'bg-gray-100'} ${
                              rowAction ? 'cursor-pointer' : ''
                            } ${info && info.deletedRow ? 'line-through' : ''} ${
                              info.boldRow ? 'font-bold' : ''
                            } ${
                              info.borderTop ? 'border-t border-gray-500' : ''
                            } ${
                              info.borderBottom
                                ? 'border-b border-gray-500'
                                : ''
                            } ${info.rowColor ? `bg-${info.rowColor}` : ''} `}
                            onClick={() => rowAction && rowAction(info.rowId)}
                          >
                            {selectedTableTitles &&
                              selectedTableTitles.find(
                                (selectTitle) => selectTitle?.id === 0
                              ) && (
                                <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                  <div className="inline-flex items-center gap-2">
                                    {info.payload[tableTitles[0]]}
                                    {info.childRows && (
                                      <span
                                        className={
                                          'flex h-4 w-4 cursor-pointer items-center justify-center rounded-full ' +
                                          'border-2 border-gray-400'
                                        }
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          if (info.rowId == null) return
                                          changeChildRow(info.rowId)
                                        }}
                                      >
                                        {!childRowsAlwaysActive ||
                                        childRowsAlwaysActive === undefined ? (
                                          currentParent === info.rowId ? (
                                            <MinusIcon className="h-full font-bold" />
                                          ) : (
                                            <PlusIcon className="h-full font-bold" />
                                          )
                                        ) : currentParent === info.rowId ? (
                                          <MinusIcon className="h-full font-bold" />
                                        ) : (
                                          <PlusIcon className="h-full font-bold" />
                                        )}
                                      </span>
                                    )}
                                  </div>
                                </td>
                              )}

                            {tableTitles.slice(1).map((item, index) => {
                              if (
                                showSpecificColumns !== undefined &&
                                showSpecificColumns
                              ) {
                                if (
                                  selectedTableTitles &&
                                  selectedTableTitles.find(
                                    (selectTitle) => selectTitle?.name === item
                                  )
                                ) {
                                  return lastColumnInGray ? (
                                    <td
                                      className={
                                        'max-w-xs text-ellipsis break-words ' +
                                        'px-3 py-4 text-center text-sm text-gray-500' +
                                        (tableTitles.length === index + 2
                                          ? `border-b bg-gray-300 font-semibold text-slate-600 ${
                                              info.borderTop &&
                                              tableTitles.length === index + 2
                                                ? 'border-t border-t-gray-500'
                                                : ''
                                            } ${
                                              info.borderBottom &&
                                              tableTitles.length === index + 2
                                                ? 'border-b border-gray-500'
                                                : ''
                                            }`
                                          : '')
                                      }
                                      key={index}
                                    >
                                      {info.payload[item]}
                                    </td>
                                  ) : (
                                    <td
                                      className={
                                        'max-w-xs text-ellipsis break-words ' +
                                        'px-3 py-4 text-center text-sm text-gray-500' +
                                        `${columnClassnames?.find((i) => i.columnName === item)?.className || ''}`
                                      }
                                      key={index}
                                    >
                                      {info.payload[item]}
                                    </td>
                                  )
                                }
                              } else {
                                return lastColumnInGray ? (
                                  <td
                                    className={
                                      'max-w-xs text-ellipsis break-words ' +
                                      'px-3 py-4 text-center text-sm text-gray-500' +
                                      (tableTitles.length === index + 2
                                        ? `border-b bg-gray-300 font-semibold text-slate-600 ${
                                            info.borderTop &&
                                            tableTitles.length === index + 2
                                              ? 'border-t border-t-gray-500'
                                              : ''
                                          } ${
                                            info.borderBottom &&
                                            tableTitles.length === index + 2
                                              ? 'border-b border-gray-500'
                                              : ''
                                          }`
                                        : '')
                                    }
                                    key={index}
                                  >
                                    {info.payload[item]}
                                  </td>
                                ) : (
                                  <td
                                    className={
                                      'max-w-xs text-ellipsis break-words ' +
                                      'px-3 py-4 text-center text-sm text-gray-500'
                                    }
                                    key={index}
                                  >
                                    {info.payload[item]}
                                  </td>
                                )
                              }
                            })}
                            {rowAction && (
                              <td className="py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                <span className="flex justify-end text-slate-600 hover:text-indigo-900">
                                  {info.rowWihtoutIcon !== undefined ? (
                                    info.rowWihtoutIcon ? (
                                      ''
                                    ) : rowActionDeleteIcon ? (
                                      <TrashIcon className="h-5 text-red-500" />
                                    ) : (
                                      <ChevronRightIcon className="h-5" />
                                    )
                                  ) : rowActionDeleteIcon ? (
                                    <TrashIcon className="h-5 text-red-500" />
                                  ) : (
                                    <ChevronRightIcon className="h-5" />
                                  )}
                                </span>
                              </td>
                            )}
                          </tr>

                          {info.childRows &&
                            info.childRows.map((subItem, idx) => (
                              <tr
                                className={`parent-${info.rowId} ${idx % 2 === 0 ? '' : 'bg-gray-100'} cursor-pointer`}
                                hidden={
                                  !childRowsAlwaysActive ||
                                  childRowsAlwaysActive === undefined
                                    ? true
                                    : false
                                }
                                key={idx}
                                onClick={() =>
                                  childRowAction &&
                                  childRowAction(
                                    subItem?.rowId || '',
                                    info.rowId
                                  )
                                }
                              >
                                <td className="py-2 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-10">
                                  {subItem?.payload[tableTitles[0] || ''] || ''}
                                </td>
                                {tableTitles.slice(1).map((item, index) => {
                                  if (
                                    showSpecificColumns !== undefined &&
                                    showSpecificColumns
                                  ) {
                                    if (
                                      selectedTableTitles.filter(
                                        (element) => element.name === item
                                      ).length > 0
                                    ) {
                                      return (
                                        <td
                                          className="text-ellipsis px-3 py-2 text-center text-sm text-gray-500"
                                          key={index}
                                        >
                                          {subItem?.payload[item] || ''}
                                        </td>
                                      )
                                    } else {
                                      return (
                                        <td
                                          className="text-ellipsis px-3 py-2 text-center text-sm text-gray-500"
                                          key={index}
                                        >
                                          {subItem?.payload[item] || ''}
                                        </td>
                                      )
                                    }
                                  } else {
                                    return (
                                      <td
                                        className="text-ellipsis px-3 py-2 text-center text-sm text-gray-500"
                                        key={index}
                                      >
                                        {subItem?.payload[item] || ''}
                                      </td>
                                    )
                                  }
                                })}

                                {rowAction && (
                                  <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                    <span className="sr-only"></span>
                                  </td>
                                )}
                              </tr>
                            ))}
                        </Fragment>
                      ))
                    ) : (
                      <tr className={` ${classRowVoid ? classRowVoid : ``}`}>
                        <td
                          className={`px-3 py-5 text-sm text-gray-500`}
                          colSpan={tableTitles.length + 1}
                        >
                          <div className="flex h-full w-full flex-col items-center justify-center p-24">
                            <EmptyList />
                            <div className="flex w-full items-center justify-center gap-2">
                              {!!addAction && !filterActived && (
                                <div className="-mt-4">
                                  <Button
                                    action={addAction.action}
                                    color="slate-500"
                                    icon={addAction.icon}
                                    name={addAction.title}
                                  />
                                </div>
                              )}
                              {filterActived && (
                                <div className="-mt-4">
                                  <Button
                                    action={() => {
                                      filterComponent?.filterAction(null)
                                      setSelectedFilter([])
                                      setFilterActived(false)
                                      //setFilterValue({});
                                      // setIsFilterActive(false);
                                    }}
                                    color="slate-500"
                                    icon={<FunnelIcon className="h-5" />}
                                    name="Limpiar filtro"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
            {paginateComponent && paginateComponent}
          </div>
        </div>
      </div>
    </ContextTable>
  )
}

export default GenericTable
